import { useState } from 'react';
import { Button, Flex, Stack, useTheme } from '@fti/design-system';
import { BillingCodeField, BILLING_CODE_REGEX } from './BillingCodeField';
import { useUpdateProject } from '../../../api/media-sim/projects';

interface EditableBillingCodeProps {
  projectId: string;
  billingCode: string | null;
  /** Called after a successful save so the caller can refresh. */
  onSaved?: () => void;
}

/**
 * Shows a project's billing code with an inline edit.
 *
 * Projects frequently have to be set up before accounting has raised the
 * client code, so people start on a practice or new business code and correct
 * it later. Billing code is the only field that stays editable after a project
 * is registered; everything else feeds the generation pipeline and is locked
 * once results exist.
 */
export function EditableBillingCode({
  projectId,
  billingCode,
  onSaved,
}: EditableBillingCodeProps) {
  const { theme } = useTheme();
  const { updateProject } = useUpdateProject();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(billingCode || '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // The project is fetched by ProjectScopeGate, which does not expose a
  // refetch, and it is shared by several subpages. Rather than widen that
  // component's API, hold the saved value here so the display updates
  // immediately after a successful save.
  const [current, setCurrent] = useState(billingCode);

  const labelStyle = {
    fontSize: '0.8rem',
    color: theme.colors.text.secondary,
    fontWeight: 600 as const,
    minWidth: '110px',
  };
  const fieldStyle = { fontSize: '0.9rem', color: theme.colors.text.primary };

  const open = () => {
    setDraft(current || '');
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    // Mirror the API's validation so a bad value is caught before a round trip.
    // The API rejects clearing the code entirely, since a project cannot be
    // created without one.
    if (!draft.trim()) {
      setError('A billing code is required. Use a practice code if the client code does not exist yet.');
      return;
    }
    if (!BILLING_CODE_REGEX.test(draft)) {
      setError('Format must be NNNNNN.NNNN-NNN');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      // Display what the server confirms it stored, not what was sent, so the
      // screen can never disagree with the database.
      const saved = await updateProject(projectId, { billing_code: draft });
      setCurrent(saved?.billing_code ?? draft);
      setEditing(false);
      onSaved?.();
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      setError(apiErr?.message || 'Failed to update the billing code.');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <Flex gap="sm" align="center">
        <span style={labelStyle}>Billing Code:</span>
        <span style={fieldStyle}>{current || '—'}</span>
        <Button variant="ghost" size="sm" onClick={open}>
          Edit
        </Button>
      </Flex>
    );
  }

  return (
    <Stack gap="xs">
      <span style={labelStyle}>Billing Code</span>
      <BillingCodeField
        value={draft}
        onChange={(code) => {
          setDraft(code);
          // Clear a stale validation message as soon as the value changes,
          // otherwise picking a valid code from the dropdown leaves the old
          // error on screen until Save is pressed.
          setError(null);
        }}
        error={error}
        disabled={saving}
      />
      <Flex gap="sm">
        <Button variant="solid" color="primary" size="sm" onClick={save} loading={saving}>
          Save
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setEditing(false);
            setError(null);
          }}
          disabled={saving}
        >
          Cancel
        </Button>
      </Flex>
    </Stack>
  );
}
